<script setup lang="ts">
import {
  RuleExpression,
  useField,
  useFormValues,
  useSetFieldValue,
} from 'vee-validate'
import { MaybeRef, computed, onMounted, toValue, watch } from 'vue'
import CurrencyInput from '@/components/CurrencyInput/CurrencyInput.vue'
import { CurrencyInputOptions } from 'vue-currency-input'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { useAttrs, ref } from 'vue'
import PhoneInput from './PhoneInput.vue'
import Select from './Select.vue'
import { useMenuPosition } from '@/composables/useMenuPosition'
import greenInfoIcon from '@/assets/icons/green/info.svg'
import Tooltip from './Tooltip.vue'

const attrs = useAttrs()
const theme = useTheme()
const { t } = useI18n()

defineOptions({ inheritAttrs: false })

const isDate = computed(() => props.type === 'date')
const isTime = computed(() => props.type === 'time')

const props = withDefaults(
  defineProps<{
    name: string
    id?: string
    type?: string
    counter?: boolean | undefined
    rules?: MaybeRef<RuleExpression<unknown>>
    label?: string
    items?: Array<unknown>
    labelField?: string
    valueField?: string
    disabled?: boolean
    checked?: boolean
    defaultValue?: string
    loading?: boolean
    icon?: string
    leftPosition?: boolean
    onClickButton?: () => void
    decimalPlaces?: number
    hideErrorMessage?: boolean
    error?: string
    placeholder?: string
    emptyState?: boolean
    labelValue?: boolean
    currencyOptions?: CurrencyInputOptions
    min?: number | string
    max?: number | string
    modelValue?: unknown
    appendInner?: boolean
    prependInner?: boolean
    events?: boolean
    highlightValidation?: boolean
    density?: 'default' | 'comfortable' | 'compact'
    /** Ativa/desativa o default aleatório para colorType quando vazio */
    randomizeColorDefault?: boolean
    allowNegative?: boolean | null
    maska?: object | null
    description?: string | null
    required?: boolean
    tooltip?: string | null
    filter?: boolean
    clearable?: boolean
    flag?: boolean
    hasRules?: boolean
    showValidationErrors?: boolean
  }>(),
  {
    id: '',
    type: 'text',
    counter: undefined,
    labelField: 'name',
    valueField: 'value',
    rules: '',
    label: '',
    checked: false,
    icon: '',
    leftPosition: false,
    decimalPlaces: undefined,
    placeholder: undefined,
    emptyState: true,
    labelValue: false,
    currencyOptions: undefined,
    modelValue: undefined,
    appendInner: false,
    prependInner: false,
    events: false,
    highlightValidation: false,
    items: () => [],
    density: 'comfortable',
    defaultValue: undefined,
    onClickButton: undefined,
    error: undefined,
    min: undefined,
    max: undefined,
    randomizeColorDefault: true,
    allowNegative: false,
    maska: null,
    description: '',
    required: false,
    tooltip: undefined,
    filter: false,
    clearable: false,
    flag: false,
    hasRules: false,
    showValidationErrors: true,
  }
)

const emit = defineEmits([
  'change',
  'country-code',
  'validate',
  'blur',
  'input',
  'update:modelValue',
])

const { openMenu, closeMenu } = useMenuPosition()

const hintText = computed(() => props.description ?? undefined)

const { value, errorMessage, setValue, meta, setTouched, handleBlur } =
  useField(
    () => props.name,
    props.type === 'checkbox' ? undefined : props.rules,
    {
      initialValue:
        props.type === 'checkbox'
          ? undefined
          : (props.defaultValue ?? undefined),
      validateOnMount: false,
      validateOnValueUpdate: false,
    }
  )

const phoneModel = computed<string | undefined>({
  get: () => (typeof value.value === 'string' ? value.value : undefined),
  set: (v) => setValue(v),
})

const maxLength = computed(() => Number(toValue(attrs.maxlength ?? 0)))
const values = useFormValues()
const setFieldValue = useSetFieldValue(props.name)

/** Paleta Vuetify (tons 500) — conforme https://vuetifyjs.com/en/styles/colors/#material-colors */
const VUETIFY_MATERIAL_500: readonly string[] = [
  '#F44336', // red
  '#E91E63', // pink
  '#9C27B0', // purple
  '#673AB7', // deep-purple
  '#3F51B5', // indigo
  '#2196F3', // blue
  '#03A9F4', // light-blue
  '#00BCD4', // cyan
  '#009688', // teal
  '#4CAF50', // green
  '#8BC34A', // light-green
  '#CDDC39', // lime
  '#FFEB3B', // yellow
  '#FFC107', // amber
  '#FF9800', // orange
  '#FF5722', // deep-orange
  '#795548', // brown
  '#607D8B', // blue-grey
  '#9E9E9E', // grey
]

function pickRandom<T>(arr: readonly T[]): T {
  const i = Math.floor(Math.random() * arr.length)
  return arr[i]
}

// ---------- COLOR PICKER ----------
const selectedColor = ref<string>(String(value.value ?? ''))
const showPicker = ref(false)

/** Define cor default aleatória (apenas quando vazio) */
onMounted(() => {
  // currency: sincroniza com form se necessário
  if (props.type === 'currency' && value.value !== values.value) {
    setFieldValue(value.value)
  }

  if (
    props.type === 'colorType' &&
    props.randomizeColorDefault &&
    (!value.value || String(value.value).trim() === '')
  ) {
    const fallback = pickRandom(VUETIFY_MATERIAL_500)
    setValue(fallback)
    selectedColor.value = fallback
  }
})

watch(value, (val) => {
  if (typeof val === 'string') selectedColor.value = val
})

function onColorSelected(color: string) {
  selectedColor.value = color
  setValue(color)
  showPicker.value = false
}

const dateFocus = ref(false)
const dateMenu = ref(false)
const initialDate = computed<Date | null>(() => {
  const v = value.value
  if (v instanceof Date) return v
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v as string | number)
    return isNaN(d.getTime()) ? null : d
  }
  return null
})
const dateValue = ref<Date | null>(initialDate.value)

const showCounter = computed(() => {
  const length = toValue(attrs.maxlength)
  return length !== undefined && length !== null
})

const forwarded = computed<Record<string, unknown>>(() => {
  const a: Record<string, unknown> = { ...(attrs as Record<string, unknown>) }
  if (
    props.type === 'number' ||
    props.type === 'date' ||
    props.type === 'time'
  ) {
    delete a.maxlength
    delete a.maxLength
    delete a.counter
  }
  return a
})

const label = computed(() => {
  if (!props.label) return ''

  const cleanLabel = props.label.replace(/(?:\s*\*)+\s*$/, '')
  const isRequired = props.required || meta.required

  return `${cleanLabel}${isRequired ? '*' : ''}`
})

const error = computed(() => {
  if (props.error) return props.error
  return meta.touched && errorMessage.value ? t(errorMessage.value) : undefined
})

const swatchStyle = computed(() => ({
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  backgroundColor: String(selectedColor.value || '#4CAF50'),
  cursor: 'pointer',
}))

const handleFocusMenu = (isOpen: boolean) => {
  dateFocus.value = isOpen
}

const onDateChange = (val: string | Date | null) => {
  if (val === null) {
    dateValue.value = null
    displayDate.value = ''
    setValue(null)
    emit('input', null)
    dateMenu.value = false
    return
  }
  const d = val instanceof Date ? val : (parseIsoLocal(val) ?? new Date(val))
  if (!d || isNaN(d.getTime())) return
  dateValue.value = d
  displayDate.value = toDisplay(d)
  const iso = toIsoLocal(d)
  setValue(iso)
  emit('input', iso)
  dateMenu.value = false
}

function handleChange(newValue: string | number | boolean | Event | null) {
  if (!meta.touched && newValue) setTouched(true)

  if (newValue instanceof Event) {
    const target = newValue.target as HTMLInputElement
    newValue = target.value
  }

  setValue(newValue) // Update the vee-validate form state
  emit('change', newValue) // Emit the change event
  emit('update:modelValue', newValue) // Emit the model update event  showPicker.value = false
}

watch(
  () => props.modelValue,
  (newValue) => setValue(newValue as string | number | boolean | null)
)

watch(value, (newValue) => {
  if (
    props.type !== 'date' &&
    props.type !== 'time' &&
    typeof newValue === 'string' &&
    maxLength.value &&
    newValue.length > maxLength.value
  ) {
    setValue(newValue.slice(0, maxLength.value))
  }
  emit('update:modelValue', newValue)
})

const pad2 = (n: number) => String(n).padStart(2, '0')

function parseIsoLocal(iso?: string | null): Date | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const [y, m, d] = iso.split('-').map((v) => Number(v))
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}

function toIsoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function toDisplay(d: Date): string {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
}

watch(
  () => value.value,
  (v) => {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const d = new Date(`${v}T00:00:00`)
      if (!isNaN(d.getTime())) dateValue.value = d
    }
  },
  { immediate: true }
)

const displayDate = ref<string>('')

function syncFromIso(iso?: string | null) {
  const d = parseIsoLocal(iso ?? null)
  dateValue.value = d
  displayDate.value = d ? toDisplay(d) : ''
}

onMounted(() => {
  if (typeof value.value === 'string') syncFromIso(value.value)
})

watch(
  () => value.value,
  (v) => {
    if (typeof v === 'string') syncFromIso(v)
    else if (v === null) syncFromIso(null)
  },
  { immediate: true }
)

type SelectValue = string | number | boolean | undefined

function normalizeSelectValue(v: unknown): SelectValue {
  // se vier objeto (ex: { countryCode: 'BR', name: 'Brasil' })
  if (v && typeof v === 'object') {
    const key = props.valueField || 'value'
    const candidate = (v as Record<string, unknown>)[key]
    if (
      typeof candidate === 'string' ||
      typeof candidate === 'number' ||
      typeof candidate === 'boolean'
    ) {
      return candidate
    }
    return undefined
  }

  // se vier direto
  if (
    typeof v === 'string' ||
    typeof v === 'number' ||
    typeof v === 'boolean'
  ) {
    return v
  }

  // null/undefined
  return undefined
}

const selectModel = computed<SelectValue>({
  get: () => normalizeSelectValue(value.value),
  set: (raw) => {
    const normalized = normalizeSelectValue(raw)
    setValue(normalized)
    emit('change', normalized)
    emit('update:modelValue', normalized)
  },
})

const radioValue = computed<string | number | boolean | undefined>(() => {
  return attrs.value as string | number | boolean | undefined
})
</script>

<template>
  <div id="form-input" :class="['d-flex flex-column w-100', $attrs.class]">
    <!-- Select -->
    <Select
      v-if="type === 'select'"
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="selectModel"
      :description="hintText"
      :empty-state="props.emptyState"
      :name="name"
      :disabled="loading || disabled"
      :label="label"
      :placeholder="placeholder"
      :items="props.items"
      :error="toValue(error)"
      :label-field="props.labelField"
      :value-field="props.valueField"
      :density="props.density"
      :required="required"
      :tooltip="tooltip"
      :clearable="clearable"
    />

    <!-- Phone -->
    <PhoneInput
      v-else-if="type === 'phone'"
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="phoneModel"
      :name="name"
      :type="type"
      :label="label"
      :events="events"
      :disabled="loading || disabled"
      :error="meta.touched && !!errorMessage"
      :error-message="errorMessage || ''"
      :highlight-validation="highlightValidation"
      :density="props.density"
      @input="
        (val: string) => {
          setValue(val)
          $emit('update:modelValue', val)
        }
      "
      @country-code="(val: string) => $emit('country-code', val)"
    />

    <!-- Currency -->
    <CurrencyInput
      v-else-if="type === 'currency'"
      v-bind="{ ...$attrs }"
      :id="id"
      :value="value || { currency: 'BRL', amount: '0.00' }"
      :options="currencyOptions"
      :name="name"
      :filter="filter"
      :flag="flag"
      :disabled="loading || disabled"
      :density="props.density"
      :label="props.label"
      :placeholder="placeholder"
      :description="description"
      :required="required"
      :tooltip="tooltip"
      :min="min as number"
      :error="!!errorMessage"
      :highlight-color="
        highlightValidation && meta.touched
          ? meta.valid
            ? 'success'
            : 'error'
          : undefined
      "
      @currency="
        (e: Event) => {
          handleChange(e)
          $emit('input', e)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    />

    <!-- Date (FORÇADO TEXTO; SEM NATIVO) -->
    <v-text-field
      v-else-if="isDate"
      v-bind="forwarded"
      :id="id"
      :hint="hintText"
      :name="name"
      type="text"
      :label="label"
      :disabled="loading || disabled"
      :error="props.showValidationErrors && !!errorMessage"
      :density="props.density"
      variant="outlined"
      autocomplete="off"
      hide-details="auto"
      :append-inner="appendInner"
      :prepend-inner="prependInner"
      :model-value="displayDate"
      placeholder="dd/mm/aaaa"
      readonly
      role="button"
      inputmode="none"
      @click="handleFocusMenu(true)"
      @focus="handleFocusMenu(true)"
      @keydown.prevent
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    >
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        :persistent="false"
        activator="parent"
        @blur="dateFocus = false"
        @click:outside="handleFocusMenu(false)"
      >
        <v-date-picker
          v-model="dateValue"
          show-adjacent-months
          hide-header
          :min="min"
          color="green"
          @update:model-value="onDateChange"
        />
      </v-menu>
      <template v-if="prependInner" #prepend-inner>
        <v-icon :icon="props.icon" size="20px" color="grey-darken-2" />
      </template>
    </v-text-field>

    <!-- Time -->
    <v-text-field
      v-else-if="isTime"
      v-bind="forwarded"
      :id="id"
      v-model="value"
      :name="name"
      type="time"
      :label="label"
      :disabled="loading || disabled"
      :error="props.showValidationErrors && !!errorMessage"
      :density="props.density"
      variant="outlined"
      autocomplete="off"
      hide-details="auto"
      :append-inner="appendInner"
      :prepend-inner="prependInner"
      @input="
        (e: Event) => {
          handleChange(e)
          $emit('input', e)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    >
      <template v-if="prependInner" #prepend-inner>
        <v-icon :icon="props.icon" size="20px" color="grey-darken-2" />
      </template>
    </v-text-field>

    <!-- Color -->
    <div v-else-if="type === 'colorType'" class="d-flex align-center ga-4">
      <v-text-field
        :id="id"
        v-model="selectedColor"
        :label="label"
        :density="props.density"
        variant="outlined"
        readonly
        class="w-100"
        :disabled="loading || disabled"
        :error="!!error"
        @click:append-inner="showPicker = true"
      >
        <template #append-inner>
          <div :style="swatchStyle" />
        </template>
      </v-text-field>

      <v-menu
        v-model="showPicker"
        :close-on-content-click="false"
        activator="parent"
      >
        <v-color-picker
          v-model="selectedColor"
          mode="hexa"
          hide-inputs
          show-swatches
          @update:model-value="onColorSelected"
        />
      </v-menu>
    </div>

    <!-- Checkbox -->
    <v-checkbox
      v-else-if="type === 'checkbox'"
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="value"
      :label="label"
      :name="name"
      hide-details
      :error="!!error"
      :error-messages="typeof error === 'string' ? error : ''"
      color="green"
      density="compact"
      class="ma-0 pa-0"
      :ripple="false"
      :disabled="loading || disabled"
    />

    <!-- Radio -->

    <v-radio-group
      v-else-if="type === 'radio'"
      v-bind="{ ...$attrs }"
      :id="id"
      :name="name"
      hide-details
      color="green"
      density="compact"
      class="ma-0 pa-0"
      :disabled="loading || disabled"
      :model-value="value"
      @update:model-value="
        (v: unknown) => {
          setValue(v as string | number | boolean | null)
          emit('change', v as string | number | boolean | null)
          emit('update:modelValue', v)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          emit('blur')
        }
      "
    >
      <v-radio :label="label" :value="radioValue" :ripple="false" />
    </v-radio-group>

    <!-- Textarea -->
    <v-textarea
      v-else-if="type === 'textarea'"
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="value"
      :name="name"
      :label="label"
      :placeholder="placeholder"
      :disabled="loading || disabled"
      :density="props.density"
      :error="!!error"
      :error-messages="typeof error === 'string' ? error : ''"
      variant="outlined"
      auto-grow
      rows="3"
      class="w-100"
      :counter="attrs.maxlength ? true : undefined"
      :maxlength="$attrs.maxlength"
      :max="$attrs.maxlength"
      hide-details="auto"
      :hint="String(description)"
      :required="required"
      :highlight-color="
        highlightValidation && meta.touched
          ? meta.valid
            ? 'success'
            : 'error'
          : undefined
      "
      @input="
        (e: Event) => {
          handleChange(e)
          $emit('input', e)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    >
      <template v-if="showCounter" #counter="{ counter: slotCounter }">
        <div
          class="text-caption d-flex justify-end pr-2"
          :style="{
            color:
              typeof value === 'string' && maxLength
                ? (value as string).length === maxLength
                  ? theme.current.value.colors.error
                  : (value as string).length >= maxLength * 0.8
                    ? theme.current.value.colors.warning
                    : 'inherit'
                : 'inherit',
          }"
        >
          {{ slotCounter }}
        </div>
      </template>

      <!-- Tooltip e botão -->
      <template v-if="tooltip" #append>
        <div v-if="tooltip" class="tooltip-wrapper">
          <Tooltip :header-text="tooltip">
            <img
              id="tooltip"
              width="24"
              :src="greenInfoIcon"
              alt="info"
              @mouseenter="openMenu"
              @mouseleave="closeMenu"
            />
          </Tooltip>
        </div>
      </template>
    </v-textarea>

    <!-- Number -->
    <Input
      v-else-if="props.type === 'number'"
      v-bind="forwarded"
      :id="id"
      :key="`${name}-number`"
      v-model="value"
      v-maska="maska"
      :type="type"
      :name="name"
      :multiline="false"
      :disabled="loading || disabled"
      :density="props.density"
      :checked="checked"
      :min="min"
      :max="max"
      :label="label"
      :label-value="labelValue"
      :description="hintText"
      :error="toValue(error)"
      :icon="icon"
      :left-position="leftPosition"
      :on-click-button="onClickButton"
      :decimal-places="decimalPlaces"
      :append-inner="appendInner"
      :prepend-inner="prependInner"
      :counter="false"
      :maxlength="undefined"
      :placeholder="placeholder"
      :highlight-color="
        highlightValidation && meta.touched
          ? meta.valid
            ? 'success'
            : 'error'
          : undefined
      "
      @input="
        (e: Event) => {
          handleChange(e)
          $emit('input', e)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    />

    <!-- Input padrão -->
    <Input
      v-else-if="props.hasRules"
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="value"
      :type="type"
      :name="name"
      :description="hintText"
      :multiline="type === 'textarea'"
      :disabled="loading || disabled"
      :density="props.density"
      :checked="checked"
      :min="min"
      :max="max"
      :label="label"
      :label-value="labelValue"
      :error="toValue(error)"
      :icon="icon"
      :left-position="leftPosition"
      :on-click-button="onClickButton"
      :decimal-places="decimalPlaces"
      :append-inner="appendInner"
      :prepend-inner="prependInner"
      :counter="attrs.maxlength ? true : undefined"
      :placeholder="placeholder"
      :tooltip="tooltip"
      :clearable="clearable"
      :has-rules="true"
      :rules="rules"
      :highlight-color="
        highlightValidation && meta.touched
          ? meta.valid
            ? 'success'
            : 'error'
          : undefined
      "
      autocomplete="off"
      @input="
        (e: Event) => {
          handleChange(e)
          $emit('input', e)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    />

    <!-- Input padrão -->
    <Input
      v-else
      v-bind="{ ...$attrs }"
      :id="id"
      v-model="value"
      :type="type"
      :name="name"
      :description="hintText"
      :multiline="type === 'textarea'"
      :disabled="loading || disabled"
      :density="props.density"
      :checked="checked"
      :min="min"
      :max="max"
      :label="label"
      :label-value="labelValue"
      :error="toValue(error)"
      :icon="icon"
      :left-position="leftPosition"
      :on-click-button="onClickButton"
      :decimal-places="decimalPlaces"
      :append-inner="appendInner"
      :prepend-inner="prependInner"
      :counter="attrs.maxlength ? true : undefined"
      :placeholder="placeholder"
      :tooltip="tooltip"
      :clearable="clearable"
      :highlight-color="
        highlightValidation && meta.touched
          ? meta.valid
            ? 'success'
            : 'error'
          : undefined
      "
      autocomplete="off"
      @input="
        (e: Event) => {
          handleChange(e)
          $emit('input', e)
        }
      "
      @blur="
        (e: Event) => {
          handleBlur(e, true)
          $emit('blur')
        }
      "
    >
      <template v-if="showCounter" #counter="{ counter: counterValue }">
        <div
          class="text-caption d-flex justify-end pr-2"
          :style="{
            color:
              typeof value === 'string' && maxLength
                ? (value as string).length === maxLength
                  ? theme.current.value.colors.error
                  : (value as string).length >= maxLength * 0.8
                    ? theme.current.value.colors.warning
                    : 'inherit'
                : 'inherit',
          }"
        >
          {{ counterValue }}
        </div>
      </template>
    </Input>
  </div>
</template>

<style scoped>
.tooltip-wrapper {
  margin-left: 0px !important;
  margin-top: 20px !important;
  position: relative;
}

/* Reserva SEMPRE 24px para a área de mensagens (erro/hint/contador) */
:deep(.v-input__details) {
  min-height: 24px !important;
  padding-top: 4px !important; /* ajuste fino, opcional */
  padding-bottom: 0 !important;
}

/* Evita que o Vuetify force alturas diferentes via messages */
:deep(.v-messages) {
  min-height: 24px !important;
}

/* Centraliza melhor a linha quando há texto */
:deep(.v-messages__message) {
  line-height: 20px;
}

@media (max-width: 600px) {
  :deep(.v-input__details) {
    min-height: 16px !important;
    padding-top: 4px !important; /* ajuste fino, opcional */
    padding-bottom: 0 !important;
  }

  :deep(.v-messages) {
    min-height: 16px !important;
  }

  :deep(.v-messages__message) {
    line-height: 16px;
  }
}

.v-input__details {
  background-color: transparent !important;
  padding-inline: 5px !important;
  width: 120% !important;
}

.v-input--horizontal .v-input__append {
  margin-inline-start: 0px !important;
}

.v-input__append {
  background-color: transparent !important;
}

input[type='time']::-webkit-calendar-picker-indicator {
  display: none !important;
}

input[type='date']::-webkit-calendar-picker-indicator {
  display: none !important;
}
</style>
